import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, Percent, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DistributorDiscounts = ({ user }) => {
  const navigate = useNavigate();
  const [distributors, setDistributors] = useState([]);
  const [discountCaps, setDiscountCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingDiscounts, setEditingDiscounts] = useState({});
  const [editingTiers, setEditingTiers] = useState({});
  const [editingPricingTiers, setEditingPricingTiers] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const [globalCaps, setGlobalCaps] = useState({ tier1: 15, tier2: 10 });

  const fetchDistributors = async () => {
    try {
      setLoading(true);
      const [distributorsRes, capsRes] = await Promise.all([
        axios.get(`${API}/admin/distributor-discounts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/discount-caps`, { headers: getAuthHeaders() })
      ]);
      
      setDistributors(distributorsRes.data.discounts);
      setDiscountCaps(capsRes.data.caps || []);
      setGlobalCaps({
        tier1: capsRes.data.global_tier1_cap || 15,
        tier2: capsRes.data.global_tier2_cap || 10
      });
      
      // Initialize editing state with current values
      const initialDiscounts = {};
      const initialTiers = {};
      const initialPricingTiers = {};
      distributorsRes.data.discounts.forEach(dist => {
        initialDiscounts[dist.distributor_name] = dist.discount_percentage || 0;
        initialTiers[dist.distributor_name] = dist.tier || 1;
        initialPricingTiers[dist.distributor_name] = dist.pricing_tier || null;
      });
      setEditingDiscounts(initialDiscounts);
      setEditingTiers(initialTiers);
      setEditingPricingTiers(initialPricingTiers);
      
      setError("");
    } catch (err) {
      setError("Failed to fetch distributor discounts");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Get the cap values from the global caps state
  const getMaxCaps = () => {
    return globalCaps;
  };

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'zkteco_admin' || user.role === 'zkteco')) {
      fetchDistributors();
    } else {
      setError("Admin access required");
      setLoading(false);
    }
  }, [user]);

  const handleDiscountChange = (distributorName, value) => {
    // Parse and validate the input
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0 || numValue > 100) {
      return; // Don't update if invalid
    }
    
    setEditingDiscounts(prev => ({
      ...prev,
      [distributorName]: numValue
    }));
  };

  const handleTierChange = (distributorName, tier) => {
    setEditingTiers(prev => ({
      ...prev,
      [distributorName]: parseInt(tier)
    }));
  };

  const handlePricingTierChange = (distributorName, pricingTier) => {
    setEditingPricingTiers(prev => ({
      ...prev,
      [distributorName]: pricingTier
    }));
  };

  const handleSaveDiscount = async (distributor) => {
    setError("");
    setSuccess("");
    
    const newDiscount = editingDiscounts[distributor.distributor_name];
    const newTier = editingTiers[distributor.distributor_name];
    const newPricingTier = editingPricingTiers[distributor.distributor_name];
    
    // Check if values actually changed
    const discountChanged = newDiscount !== distributor.discount_percentage;
    const tierChanged = newTier !== distributor.tier;
    const pricingTierChanged = newPricingTier !== distributor.pricing_tier;
    
    if (!discountChanged && !tierChanged && !pricingTierChanged) {
      setSuccess(`No changes for ${distributor.distributor_name}`);
      setTimeout(() => setSuccess(""), 2000);
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/distributor-discounts/${distributor.distributor_name}`,
        {
          discount_percentage: newDiscount,
          tier: newTier,
          pricing_tier: newPricingTier
        },
        { headers: getAuthHeaders() }
      );
      
      const tierLabel = newPricingTier === 'main_channel' ? 'Main Channel' : 
                       newPricingTier === 'main_sub_distribution' ? 'Main Sub-Dist' :
                       newPricingTier === 'sub_distribution' ? 'Sub Distribution' : 'Legacy';
      setSuccess(`✅ Updated ${distributor.distributor_name}: ${newDiscount}% discount, Tier ${newTier}, ${tierLabel}`);
      fetchDistributors();
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update");
      setTimeout(() => setError(""), 5000);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <p className="text-gray-600">Loading distributors...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'admin' && user.role !== 'zkteco_admin' && user.role !== 'zkteco')) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertDescription>Admin access required</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <Percent className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Distributor Discount Management</h1>
          </div>
          <p className="text-gray-600">
            Set discount percentages for each distributor organization. All users in that organization will receive this discount off the installer price.
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {success && (
          <Alert className="mb-4 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Distributors Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Distributor Organizations ({distributors.length})
            </CardTitle>
            <CardDescription>
              Set organization-wide discount percentages. All users in the organization share the same discount.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {distributors.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No distributors found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Distributor Organization</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Users</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Discount %</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Tier</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Pricing Tier</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distributors.map((dist) => (
                      <tr key={dist.distributor_name} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-900">
                            {dist.distributor_name}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline" className="text-xs">
                            {dist.user_count} {dist.user_count === 1 ? 'user' : 'users'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={editingDiscounts[dist.distributor_name] || 0}
                              onChange={(e) => handleDiscountChange(dist.distributor_name, e.target.value)}
                              className="w-24 text-center"
                            />
                            <span className="text-gray-500">%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <select
                            value={editingTiers[dist.distributor_name] || 1}
                            onChange={(e) => handleTierChange(dist.distributor_name, e.target.value)}
                            className="w-24 h-9 text-center border rounded-md"
                          >
                            <option value="1">Tier 1</option>
                            <option value="2">Tier 2</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <select
                            value={editingPricingTiers[dist.distributor_name] || ''}
                            onChange={(e) => handlePricingTierChange(dist.distributor_name, e.target.value || null)}
                            className="w-44 h-9 text-center border rounded-md text-sm"
                          >
                            <option value="">Legacy (Use Discount %)</option>
                            <option value="main_channel">Main Dist - Channel (-25%)</option>
                            <option value="main_sub_distribution">Main Dist - Sub Dist</option>
                            <option value="sub_distribution">Sub Distribution (-20%)</option>
                          </select>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button
                            size="sm"
                            onClick={() => handleSaveDiscount(dist)}
                            disabled={editingDiscounts[dist.distributor_name] === dist.discount_percentage && editingTiers[dist.distributor_name] === dist.tier && editingPricingTiers[dist.distributor_name] === dist.pricing_tier}
                            className="gap-2"
                          >
                            <Save className="h-4 w-4" />
                            Save
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-blue-900 mb-2">💡 How Discount Pricing Works</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Installer Price</strong> is the base price (e.g., £4,128)</li>
              <li>• <strong>Organization Discount</strong> is subtracted from installer price for all users in that organization</li>
              <li>• <strong>Tier System:</strong> Controls maximum discount on lower-margin products (e.g., Comet bases)</li>
              <li>• <strong>Tier 1:</strong> Main distributors - max {getMaxCaps().tier1}% on capped products</li>
              <li>• <strong>Tier 2:</strong> Sub-distributors - max {getMaxCaps().tier2}% on capped products</li>
              <li>• Example: Nimans at 20% discount but Tier 2 → gets 20% on most products, {getMaxCaps().tier2}% on capped products</li>
              <li>• Changes take effect immediately for all users in the organization</li>
            </ul>
            {discountCaps.length > 0 && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-xs text-blue-700">
                  <strong>Capped Products ({discountCaps.length}):</strong> {discountCaps.map(c => c.sku).join(', ')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DistributorDiscounts;
